import { useRef } from 'react'
import { useDispatch } from 'react-redux'
import { useFormContext } from 'react-hook-form'
import { Grid, Stack, Typography, Button } from '@mui/material'

import { useUploadOrgImageMutation } from 'services/assets'
import GenericImage from 'components/generic/image-preview/GenericImage'
import { dispatchSnackbar } from 'utils/helpers'

export interface SettingsUploadProps {
  title: string
  subTitle?: string
  subTitle2?: string
  buttonCaption?: string
  path: string
}

const MAX_FILE_SIZE = 5 * 1024 * 1024 // 5MB

const SettingsUpload: React.FC<SettingsUploadProps> = ({
  title,
  subTitle = '',
  subTitle2 = '',
  buttonCaption = 'Upload',
  path,
}) => {
  const dispatch = useDispatch()
  const fileInputRef = useRef<HTMLInputElement | null>(null)
  const [uploadOrgImage, { isLoading }] = useUploadOrgImageMutation()
  const { setValue, watch } = useFormContext()
  const watchCheckSignatureImage = watch(path)

  const renderContent = () => {
    return (
      <div>
        <GenericImage imageFilename={watchCheckSignatureImage} isLoading={isLoading} />
      </div>
    )
  }

  const handleUploadClick = () => {
    fileInputRef.current?.click()
  }

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files ? event.target.files[0] : null
    if (!file) return

    if (event.target.files && event.target.files.length > 1) {
      Object.defineProperty(event.target, 'files', { value: [] })
      dispatchSnackbar(dispatch, 'error', 'Please select only 1 file.')
      return
    }

    if (!file.type.startsWith('image/')) {
      Object.defineProperty(event.target, 'files', { value: [] })
      dispatchSnackbar(dispatch, 'error', 'File type not supported. Please upload an image.')
      return
    }

    // S3 limit is 5MB
    if (file.size > MAX_FILE_SIZE) {
      Object.defineProperty(event.target, 'files', { value: [] })
      dispatchSnackbar(dispatch, 'error', 'Please choose a file smaller than 5MB.')
      return
    }

    const formData = new FormData()
    formData.append('image', file)

    try {
      const response = await uploadOrgImage({ body: formData }).unwrap()
      setValue(path, response.imageName, { shouldDirty: true }) // set returned S3 image name as what we save in settings
    } catch {
      dispatchSnackbar(dispatch, 'error', 'An error occurred while uploading your photo')
    }
  }

  return (
    <>
      <Typography variant="h6" color="textPrimary" data-testid="title">
        {title}
      </Typography>
      <Typography variant="body2" color="textSecondary" data-testid="subtitle">
        {subTitle}
      </Typography>
      <Typography variant="body2" color="textSecondary" data-testid="subtitle2">
        {subTitle2}
      </Typography>
      <Grid flexWrap="wrap" container>
        {renderContent()}
      </Grid>
      <Stack mt={1} direction="row" spacing={1}>
        <Button variant="outlined" onClick={handleUploadClick} data-testid="upload-button">
          {buttonCaption}
        </Button>
        <input
          type="file"
          ref={fileInputRef}
          accept=".jpg, .jpeg, .png, .bmp"
          style={{ display: 'none' }}
          onChange={handleFileChange}
          data-testid="file-input"
        />
      </Stack>
    </>
  )
}

export default SettingsUpload
